import React, { useState, useEffect } from 'react';
import { Shape } from '../../types/Shape';

interface ResizeHandlesProps {
  shape: Shape;
  stageScale: number;
  onResize: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  onResizeStart?: (shapeId: string) => void;
  onResizeEnd: () => void;
}

type HandlePosition = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const ResizeHandles: React.FC<ResizeHandlesProps> = ({
  shape,
  stageScale,
  onResize,
  onResizeStart,
  onResizeEnd,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<HandlePosition | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, shapeX: 0, shapeY: 0 });

  const handleSize = 10; // 고정 크기

  const handles: { pos: HandlePosition; cursor: string }[] = [
    { pos: 'nw', cursor: 'nwse-resize' },
    { pos: 'n', cursor: 'ns-resize' },
    { pos: 'ne', cursor: 'nesw-resize' },
    { pos: 'e', cursor: 'ew-resize' },
    { pos: 'se', cursor: 'nwse-resize' },
    { pos: 's', cursor: 'ns-resize' },
    { pos: 'sw', cursor: 'nesw-resize' },
    { pos: 'w', cursor: 'ew-resize' },
  ];

  const handleMouseDown = (e: React.MouseEvent, pos: HandlePosition) => {
    e.stopPropagation();
    e.preventDefault();

    startResize(pos, e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent, pos: HandlePosition) => {
    e.stopPropagation();
    // Prevent default to stop scrolling/zooming while interacting with the handle
    // e.preventDefault(); // React synthetic event preventDefault might not be enough for passive listeners? 
    // But typically for touchstart on a button it works if non-passive.
    // However, we'll try standard way first.

    const touch = e.touches[0];
    startResize(pos, touch.clientX, touch.clientY);
  };

  const startResize = (pos: HandlePosition, clientX: number, clientY: number) => {
    // 리사이즈 시작 시 히스토리 저장
    if (onResizeStart) {
      onResizeStart(shape.id);
    }

    setIsResizing(true);
    setResizeHandle(pos);
    setResizeStart({
      x: clientX,
      y: clientY,
      width: shape.width || 100,
      height: shape.height || (shape.radius ? shape.radius * 2 : 100),
      shapeX: shape.x,
      shapeY: shape.y,
    });
  };

  useEffect(() => {
    if (!isResizing || !resizeHandle) return;

    const handleMove = (clientX: number, clientY: number, shiftKey: boolean) => {
      const dx = (clientX - resizeStart.x) / stageScale;
      const dy = (clientY - resizeStart.y) / stageScale;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.shapeX;
      let newY = resizeStart.shapeY;

      // 원본 비율 계산
      const aspectRatio = resizeStart.width / resizeStart.height;
      const isShiftPressed = shiftKey;

      // 핸들 위치에 따라 크기 조정
      switch (resizeHandle) {
        case 'se': // 오른쪽 아래
          newWidth = Math.max(20, resizeStart.width + dx);
          newHeight = Math.max(20, resizeStart.height + dy);
          if (isShiftPressed) {
            // Shift: 비율 유지 (더 큰 변화를 기준으로)
            if (Math.abs(dx) > Math.abs(dy)) {
              newHeight = newWidth / aspectRatio;
            } else {
              newWidth = newHeight * aspectRatio;
            }
          }
          break;
        case 'nw': // 왼쪽 위
          newWidth = Math.max(20, resizeStart.width - dx);
          newHeight = Math.max(20, resizeStart.height - dy);
          if (isShiftPressed) {
            if (Math.abs(dx) > Math.abs(dy)) {
              newHeight = newWidth / aspectRatio;
            } else {
              newWidth = newHeight * aspectRatio;
            }
          }
          newX = resizeStart.shapeX + (resizeStart.width - newWidth);
          newY = resizeStart.shapeY + (resizeStart.height - newHeight);
          break;
        case 'ne': // 오른쪽 위
          newWidth = Math.max(20, resizeStart.width + dx);
          newHeight = Math.max(20, resizeStart.height - dy);
          if (isShiftPressed) {
            if (Math.abs(dx) > Math.abs(dy)) {
              newHeight = newWidth / aspectRatio;
            } else {
              newWidth = newHeight * aspectRatio;
            }
          }
          newY = resizeStart.shapeY + (resizeStart.height - newHeight);
          break;
        case 'sw': // 왼쪽 아래
          newWidth = Math.max(20, resizeStart.width - dx);
          newHeight = Math.max(20, resizeStart.height + dy);
          if (isShiftPressed) {
            if (Math.abs(dx) > Math.abs(dy)) {
              newHeight = newWidth / aspectRatio;
            } else {
              newWidth = newHeight * aspectRatio;
            }
          }
          newX = resizeStart.shapeX + (resizeStart.width - newWidth);
          break;
        case 'e': // 오른쪽
          newWidth = Math.max(20, resizeStart.width + dx);
          if (isShiftPressed) {
            newHeight = newWidth / aspectRatio;
          }
          break;
        case 'w': // 왼쪽
          newWidth = Math.max(20, resizeStart.width - dx);
          newX = resizeStart.shapeX + (resizeStart.width - newWidth);
          if (isShiftPressed) {
            newHeight = newWidth / aspectRatio;
          }
          break;
        case 's': // 아래
          newHeight = Math.max(20, resizeStart.height + dy);
          if (isShiftPressed) {
            newWidth = newHeight * aspectRatio;
          }
          break;
        case 'n': // 위
          newHeight = Math.max(20, resizeStart.height - dy);
          newY = resizeStart.shapeY + (resizeStart.height - newHeight);
          if (isShiftPressed) {
            newWidth = newHeight * aspectRatio;
          }
          break;
      }

      onResize(shape.id, newWidth, newHeight, newX, newY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY, e.shiftKey);
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Prevent scrolling during resize
      if (e.cancelable) e.preventDefault();

      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY, false); // No shift key on touch usually
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeHandle(null);
      onResizeEnd();
    };

    const handleTouchEnd = () => {
      setIsResizing(false);
      setResizeHandle(null);
      onResizeEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Add non-passive listeners for touch to allow preventDefault
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isResizing, resizeHandle, resizeStart, stageScale, shape.id, onResize, onResizeStart, onResizeEnd]);

  const getHandleStyle = (pos: HandlePosition): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      width: handleSize,
      height: handleSize,
      backgroundColor: '#3B82F6',
      border: '2px solid white',
      borderRadius: '50%',
      cursor: handles.find(h => h.pos === pos)?.cursor,
      zIndex: 10,
    };

    const offset = -handleSize / 2;

    switch (pos) {
      case 'nw':
        return { ...baseStyle, top: offset, left: offset };
      case 'n':
        return { ...baseStyle, top: offset, left: '50%', transform: 'translateX(-50%)' };
      case 'ne':
        return { ...baseStyle, top: offset, right: offset };
      case 'e':
        return { ...baseStyle, top: '50%', right: offset, transform: 'translateY(-50%)' };
      case 'se':
        return { ...baseStyle, bottom: offset, right: offset };
      case 's':
        return { ...baseStyle, bottom: offset, left: '50%', transform: 'translateX(-50%)' };
      case 'sw':
        return { ...baseStyle, bottom: offset, left: offset };
      case 'w':
        return { ...baseStyle, top: '50%', left: offset, transform: 'translateY(-50%)' };
      default:
        return baseStyle;
    }
  };

  return (
    <>
      {handles.map(({ pos }) => (
        <div
          key={pos}
          style={getHandleStyle(pos)}
          onMouseDown={(e) => handleMouseDown(e, pos)}
          onTouchStart={(e) => handleTouchStart(e, pos)}
        />
      ))}
    </>
  );
};

export default ResizeHandles;

